import { useEffect, useState, useCallback } from "react";
import { notificationsApi } from "../services/api";
import { requestPermission, getPermission, isSupported } from "../services/desktopNotifications";

function Toggle({ checked, disabled, onChange, label, title }) {
  return (
    <label
      title={title}
      className={`inline-flex items-center gap-2 cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="relative w-10 h-5 rounded-full bg-gray-300 peer-checked:bg-blue-600 transition-colors">
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "right-0.5" : "right-[22px]"}`} />
      </span>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

export default function NotificationSettingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [desktopError, setDesktopError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await notificationsApi.getPreferences());
    } catch {
      setError("שגיאה בטעינת ההגדרות");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyResponse = (resp) => setData(resp);

  const updatePref = async (notification_type, patch) => {
    // optimistic
    setData((prev) => prev && {
      ...prev,
      preferences: prev.preferences.map((p) =>
        p.notification_type === notification_type ? { ...p, ...patch } : p),
    });
    try {
      applyResponse(await notificationsApi.updatePreference({ notification_type, ...patch }));
      setError(null);                                    // an earlier failure banner must not outlive a later success
    } catch {
      setError("שגיאה בשמירת ההגדרה");
      load();
    }
  };

  const onToggleInApp = (type, value) => updatePref(type, { in_app_enabled: value });

  const onToggleDesktop = async (type, value) => {
    setDesktopError(null);
    if (value) {
      const { granted, blocked, message } = await requestPermission();
      if (!granted) {
        setDesktopError(message || "יש לאשר הרשאות התראה בדפדפן");
        if (blocked) return;  // permanently blocked — do not enable
        return;               // not yet granted — leave off
      }
    }
    updatePref(type, { desktop_enabled: value });
  };

  const onChangeDisplayMode = async (mode) => {
    setData((prev) => prev && { ...prev, desktop_display_mode: mode });
    try {
      applyResponse(await notificationsApi.updateDesktopSettings(mode));
    } catch {
      setError("שגיאה בשמירת מצב תצוגת התראות מחשב");
      load();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" dir="rtl">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-20 text-red-500" dir="rtl">{error || "שגיאה"}</div>;
  }

  const permission = getPermission();

  // Group preferences by category
  const byCategory = {};
  for (const p of data.preferences) {
    (byCategory[p.category] = byCategory[p.category] || []).push(p);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" dir="rtl">
      <h1 className="text-2xl font-bold text-gray-800">הגדרות התראות</h1>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{error}</div>}

      {/* Global desktop display mode */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <h2 className="font-semibold text-gray-700">התראות מחשב (Desktop)</h2>
        {isSupported() ? (
          <>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="desktop-mode"
                checked={data.desktop_display_mode === "always"}
                onChange={() => onChangeDisplayMode("always")}
              />
              הצג התראות מחשב תמיד
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="desktop-mode"
                checked={data.desktop_display_mode === "when_tab_inactive"}
                onChange={() => onChangeDisplayMode("when_tab_inactive")}
              />
              הצג רק כשהמערכת לא בפוקוס
            </label>
            {permission === "denied" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                ההרשאה להתראות נחסמה בדפדפן — יש לאפשר התראות עבור האתר בהגדרות הדפדפן.
              </p>
            )}
            {desktopError && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {desktopError}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">הדפדפן אינו תומך בהתראות מחשב.</p>
        )}
      </div>

      {/* Per-type channel toggles, grouped by category */}
      {Object.entries(byCategory).map(([category, rows]) => (
        <div key={category} className="bg-white rounded-xl border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-700">{data.categories[category] || category}</h2>
          </div>
          <div className="divide-y">
            {rows.map((p) => (
              <div key={p.notification_type} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{p.label}</p>
                  {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <div className="flex items-center gap-5">
                    <Toggle
                      label="התראה במערכת"
                      checked={p.in_app_enabled}
                      onChange={(v) => onToggleInApp(p.notification_type, v)}
                    />
                    <Toggle
                      label="התראת מחשב"
                      checked={p.desktop_enabled}
                      disabled={!isSupported() || !p.in_app_enabled}
                      title={!p.in_app_enabled ? "יש להפעיל קודם 'התראה במערכת' עבור סוג זה" : undefined}
                      onChange={(v) => onToggleDesktop(p.notification_type, v)}
                    />
                  </div>
                  {/* A row saved before this guard existed can still hold desktop_enabled=true with
                      in_app_enabled=false — the backend never sends that notification through any
                      channel in that state, so say so rather than show a silently-inert toggle. */}
                  {p.desktop_enabled && !p.in_app_enabled && (
                    <p className="text-xs text-amber-700">
                      התראת המחשב לא תישלח כל עוד ההתראה במערכת כבויה עבור סוג זה
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
